import React from "react";
import { Link } from "react-router-dom";
import { Container } from "@/components/ui-blocks";
import { useCart } from "@/context/CartContext";
import { usePricing } from "@/hooks/usePricing";
import { Trash2, ShoppingBag } from "lucide-react";
import QuantityInput from "@/components/QuantityInput";
import BulkNudge from "@/components/BulkNudge";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal } = useCart();
  // Delivery depends on the address, which is not collected until checkout.
  // Quoting against Singapore lets the cart show the bulk offers that apply to
  // most buyers; the figure itself is only shown once the country is known.
  const { quote } = usePricing(items, "Singapore");

  if (items.length === 0) {
    return (
      <div data-testid="cart-page-empty" className="bg-[#FDFBF7]">
        <Container>
          <div className="py-32 text-center max-w-lg mx-auto">
            <div className="w-20 h-20 mx-auto border border-[#6e5022]/40 rounded-full flex items-center justify-center mb-6">
              <ShoppingBag className="w-7 h-7 text-[#6e5022]" strokeWidth={1.2} />
            </div>
            <h1 className="font-serif-display text-4xl font-light">Your cart is empty</h1>
            <p className="mt-4 text-[#4A3728]">
              Begin with the featured volume or explore upcoming collectibles.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/shop/books" data-testid="empty-cart-books-link" className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
                Shop Books
              </Link>
              <Link to="/shop/collectibles" data-testid="empty-cart-collectibles-link" className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
                Shop Collectibles
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="bg-[#FDFBF7]">
      <Container>
        <div className="py-12 md:py-16">
          <div className="overline text-[#946f36]">Shopping Cart</div>
          <h1 className="mt-3 font-serif-display text-5xl font-light">Your Cart</h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 pb-20">
          <div className="lg:col-span-8">
            <div className="border-t border-[#6e5022]/20">
              {items.map((item) => (
                <div
                  key={item.id}
                  data-testid={`cart-page-item-${item.id}`}
                  className="grid grid-cols-12 gap-4 py-6 border-b border-[#6e5022]/20 items-center"
                >
                  <Link to={`/product/${item.id}`} className="col-span-3 sm:col-span-2 block bg-[#F4F1EA] aspect-square overflow-hidden">
                    {/* An empty src makes the browser refetch the page, so skip
                        the tag entirely when a product has no image. */}
                    {item.image ? (
                      <img src={item.image} alt={item.title} className="w-full h-full object-contain p-2" />
                    ) : (
                      <div className="w-full h-full" aria-hidden="true" />
                    )}
                  </Link>
                  <div className="col-span-9 sm:col-span-5">
                    <Link to={`/product/${item.id}`} className="font-serif-display text-xl hover:text-[#6e5022]" data-testid={`cart-page-title-${item.id}`}>
                      {item.title}
                    </Link>
                    {item.sku && <div className="text-xs text-[#946f36] mt-1">SKU: {item.sku}</div>}
                    <div className="sm:hidden text-sm text-[#4A3728] mt-1">SGD {item.price.toFixed(2)}</div>
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <QuantityInput
                      value={item.qty}
                      onChange={(n) => updateQty(item.id, n)}
                      testId={`cart-page-qty-${item.id}`}
                      label={`Quantity of ${item.title}`}
                    />
                  </div>
                  <div className="hidden sm:block sm:col-span-2 font-serif-display text-xl text-right" data-testid={`cart-page-line-${item.id}`}>
                    SGD {(item.price * item.qty).toFixed(2)}
                  </div>
                  <div className="col-span-6 sm:col-span-1 text-right">
                    <button onClick={() => removeItem(item.id)} data-testid={`cart-page-remove-${item.id}`} className="text-[#946f36] hover:text-[#6e5022]" aria-label="Remove">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-4">
            <div className="bg-[#F4F1EA] border border-[#6e5022]/20 p-7">
              <div className="overline text-[#946f36] mb-4">Order Summary</div>
              <Row label="Subtotal" value={`SGD ${subtotal.toFixed(2)}`} testId="cart-summary-subtotal" />

              {quote.discountLines.map((d) => (
                <Row
                  key={d.ruleId || d.label}
                  label={d.label}
                  value={`− SGD ${d.amount.toFixed(2)}`}
                  testId={`cart-summary-discount-${d.ruleId || "line"}`}
                  tone="saving"
                />
              ))}

              <Row
                label="Delivery"
                value={quote.freeDelivery ? "Free" : "Calculated at checkout"}
                testId="cart-summary-shipping"
                tone={quote.freeDelivery ? "saving" : undefined}
              />

              <div className="border-t border-[#6e5022]/30 mt-4 pt-4 flex items-baseline justify-between">
                <span className="overline">Total</span>
                <span data-testid="cart-summary-total" className="font-serif-display text-3xl text-[#281a12]">
                  SGD {(subtotal - quote.discount).toFixed(2)}
                </span>
              </div>
              {!quote.freeDelivery && (
                <div className="text-[11px] text-[#946f36] mt-1 text-right">
                  excluding delivery
                </div>
              )}

              <BulkNudge quote={quote} testId="cart-summary-nudge" className="mt-5" />
              <Link
                to="/checkout"
                data-testid="cart-page-checkout-link"
                className="mt-6 block text-center bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Proceed to Checkout
              </Link>
              <Link to="/shop" data-testid="cart-page-continue-link" className="mt-3 block text-center text-xs uppercase tracking-[0.18em] text-[#6e5022] hover:text-[#946f36]">
                Continue Shopping
              </Link>
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}

function Row({ label, value, testId, tone }) {
  const accent = tone === "saving" ? "text-green-800" : "text-[#281a12]";
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-[#4A3728]">{label}</span>
      <span data-testid={testId} className={`${accent} text-right whitespace-nowrap`}>{value}</span>
    </div>
  );
}
